import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';

/**
 * Hook to check feature access for the current merchant
 * @param {string} featureId - The feature ID to check
 * @returns {Object} { hasAccess, loading, error }
 */
export const useFeatureAccess = (featureId) => {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Checking feature access for:', featureId);
        const response = await apiFetch(`/features/merchant/features/${featureId}/access`);
        console.log('Feature access response:', response.status, response.ok);

        if (!response.ok) {
          // For gated features, don't show error; treat as no access and prompt upgrade
          if (response.status === 403 || response.status === 404) {
            console.log('Feature access denied for:', featureId);
            setHasAccess(false);
            return;
          }
          // Auth issues bubble up as error so the shell can redirect/login
          if (response.status === 401) {
            throw new Error('Authentication required');
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Feature access data:', data);
        setHasAccess(!!(data && data.data && data.data.hasAccess));
      } catch (err) {
        console.error('Error checking feature access:', err);
        // Fail-closed without showing an error message for the UI; fallback will render upgrade prompt
        setError(null);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    if (featureId) {
      checkAccess();
    }
  }, [featureId]);

  return { hasAccess, loading, error };
};

/**
 * Hook to get all available features for the current merchant
 * @returns {Object} { features, loading, error }
 */
export const useMerchantFeatures = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFeatures = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await apiFetch('/features/merchant/features');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setFeatures(data.data || []);
      } catch (err) {
        console.error('Error loading merchant features:', err);
        setError(err.message);
        setFeatures([]);
      } finally {
        setLoading(false);
      }
    };

    loadFeatures();
  }, []);

  return { features, loading, error };
};

export default useFeatureAccess;
